import { useListingsQuery } from '../api/queries';
import type { YextListing } from '../types';

type Props = { af: string | null };

const STATUS_MAP: Record<string, { className: string; label: string }> = {
  LIVE: { className: 'live', label: 'Live' },
  SUPPRESSED: { className: 'suppressed', label: 'Suppressed' },
  WAITING_ON_PUBLISHER: { className: 'waiting', label: 'Pending' },
  OPTED_IN: { className: 'opted', label: 'Opted In' },
};

function statusFor(status: string) {
  return STATUS_MAP[status] ?? { className: 'other', label: status };
}

export default function ListingsTab({ af }: Props) {
  const { data, isLoading, error } = useListingsQuery(af);

  if (!af) {
    return (
      <div className="tab-placeholder">
        <ListingsIcon />
        <h3>Directory Listings</h3>
        <p>Select a franchisee to view their directory listings</p>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="listings-loading">
        <div className="spinner" />
        <p>Loading directory listings from Yext...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="tab-placeholder">
        <h3>Error</h3>
        <p>{(error as Error).message}</p>
      </div>
    );
  }
  if (!data?.entity) {
    return (
      <div className="tab-placeholder">
        <h3>No Location Found</h3>
        <p>
          {data?.refreshing
            ? 'Yext data is refreshing. Try again in a moment.'
            : 'No Yext location matched this franchisee. They may not have a Yext listing configured.'}
        </p>
      </div>
    );
  }

  const addr = data.entity.address ?? {};
  const addrStr = [addr.line1, addr.city, addr.region, addr.postalCode]
    .filter(Boolean)
    .join(', ');
  const live = data.summary?.live ?? 0;
  const total = data.summary?.total ?? 0;
  const liveRate = total > 0 ? Math.round((live / total) * 100) : 0;

  return (
    <>
      <div className="listings-entity-info">
        <strong>{data.entity.name}</strong>
        {addrStr && <> &mdash; {addrStr}</>}
      </div>

      <div className="listings-summary">
        <div className="listings-summary-card">
          <div className="summary-value live">{live}</div>
          <div className="summary-label">Live Listings</div>
        </div>
        <div className="listings-summary-card">
          <div className="summary-value">{total}</div>
          <div className="summary-label">Total Listings</div>
        </div>
        <div className="listings-summary-card">
          <div className="summary-value">{liveRate}%</div>
          <div className="summary-label">Live Rate</div>
        </div>
      </div>

      {data.listings && data.listings.length > 0 ? (
        <div className="listings-grid">
          {data.listings.map((lst, i) => (
            <ListingCard key={`${lst.publisher}-${i}`} listing={lst} />
          ))}
        </div>
      ) : (
        <div className="tab-placeholder">
          <p>No directory listings found for this location.</p>
        </div>
      )}

      <div className="metrics-footer">Data from Yext • Cached for 15 minutes</div>
    </>
  );
}

function ListingCard({ listing }: { listing: YextListing }) {
  const { className, label } = statusFor(listing.status);
  const cardClass = `listing-card status-${className}`;
  const inner = (
    <>
      <div className="listing-publisher">{listing.publisher}</div>
      <span className={`listing-status-badge ${className}`}>{label}</span>
    </>
  );
  if (listing.url) {
    return (
      <a href={listing.url} target="_blank" rel="noopener" className={cardClass}>
        {inner}
      </a>
    );
  }
  return <div className={cardClass}>{inner}</div>;
}

function ListingsIcon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      style={{ opacity: 0.3, marginBottom: 12 }}
    >
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9l2 2 4-4" />
    </svg>
  );
}
